import * as React from 'react';
import styled from 'styled-components';

/**
 * Horizontal scroll container for the ticket detail tab tables.
 *
 * A table's column widths are a minimum, not a cap: the design system's table lays out
 * `auto` and grows past whatever box it is given rather than squeezing its columns. On
 * the detail page that box is the content column of a two-column grid, so without a
 * scroll container the table paints straight over the Details panel beside it and the
 * right-hand columns read as missing data.
 *
 * Clipping the overflow to this box is what keeps the table inside its column. The
 * affordance telling the reader there is more to see is two-part:
 *
 * - Edge shadows via the "scroll-shadow" technique — a pair of surface-coloured
 *   gradients painted `local` (so they travel with the content) masking a pair of
 *   shadow gradients painted `scroll` (so they stay pinned to the edges). Each shadow
 *   is therefore visible only on a side that actually has content past it, with no JS
 *   and no scroll listener.
 * - An always-visible thin scrollbar. Browsers auto-hide the overlay scrollbar on
 *   mouse-driven platforms, and a hidden bar is not an affordance; styling the
 *   `::-webkit-scrollbar` pseudo-elements opts the box out of overlay scrollbars, and
 *   `scrollbar-color`/`scrollbar-width` do the same on Firefox.
 *
 * `overflow-y: hidden` is safe for the tables' own overlays — the row action menu and
 * the truncation tooltip are both portalled out of this subtree.
 */
const ScrollBox = styled.div`
	position: relative;
	overflow-x: auto;
	overflow-y: hidden;
	background: linear-gradient(to right, ${p => p.theme.sys.color.background.neutral['1']} 30%, transparent) left center / 40px
			100% no-repeat local,
		linear-gradient(to left, ${p => p.theme.sys.color.background.neutral['1']} 30%, transparent) right center / 40px 100%
			no-repeat local,
		radial-gradient(farthest-side at 0 50%, ${p => p.theme.sys.color.border.decorative}, transparent) left center / 14px
			100% no-repeat scroll,
		radial-gradient(farthest-side at 100% 50%, ${p => p.theme.sys.color.border.decorative}, transparent) right center / 14px
			100% no-repeat scroll;
	scrollbar-width: thin;
	scrollbar-color: ${p => p.theme.sys.color.border.enabled} transparent;
	&::-webkit-scrollbar {
		height: ${p => p.theme.sys.spacing['200']};
	}
	&::-webkit-scrollbar-track {
		background: transparent;
	}
	&::-webkit-scrollbar-thumb {
		background: ${p => p.theme.sys.color.border.enabled};
		border-radius: ${p => p.theme.sys.size.borderradius['3']};
	}
`;

interface Props {
	children: React.ReactNode;
	'data-testid'?: string;
}

/**
 * Wraps a tab table so a row wider than its column scrolls horizontally with a visible cue.
 *
 * Every ticket detail tab that renders a table goes through this, so the tables behave
 * the same way as each other at every width.
 */
export function ScrollableTable({children, 'data-testid': dataTestId}: Props): JSX.Element {
	return <ScrollBox data-testid={dataTestId ?? 'scrollable-table'}>{children}</ScrollBox>;
}
